import { Injectable } from '@angular/core';

import { ApprovalItem, EntityType } from '../general';

import { CoachingDiffDecorateConnector } from './coaching-diff-decorate-connector';
import { DiffItem } from './diff-item.model';

@Injectable({ providedIn: 'root' })
export class CoachingDiffDecoratorService {
    private readonly _entityTypeInstanceMap: {
        [entityType: number /* EntityType */]: CoachingDiffDecorateConnector[];
    };

    private _diffItems: DiffItem[];
    private _approvalItems: ApprovalItem[];

    constructor() {
        this._entityTypeInstanceMap = {};
    }

    /** instances implementing CoachingDiffDecorateConnector can call this */
    registerForEntityType(
        entityType: EntityType,
        instance: CoachingDiffDecorateConnector,
    ): void {
        let arr = this._entityTypeInstanceMap[entityType];
        if (!arr) {
            this._entityTypeInstanceMap[entityType] = [];
            arr = this._entityTypeInstanceMap[entityType];
        }
        arr.push(instance);

        this._decorate();
    }

    /** instances implementing CoachingDiffDecorateConnector can call this */
    unRegisterForEntityType(
        entityType: EntityType,
        instance: CoachingDiffDecorateConnector,
    ): void {
        const arr = this._entityTypeInstanceMap[entityType];
        if (!arr) {
            return;
        }
        const index = arr.indexOf(instance);
        if (index !== -1) {
            arr.splice(index, 1);
        }
    }

    /** instances implementing CoachingDiffDecorateConnector can call this */
    redecorate(instance: CoachingDiffDecorateConnector): void {
        this._runSingle(instance, (diffItem) => {
            instance.ddcUnDecorate &&
                ['change', 'accept', 'dispute'].forEach((state) => {
                    instance.ddcUnDecorate(diffItem, state as any);
                });
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            instance.ddcDecorate &&
                instance.ddcDecorate(diffItem, this._approvalState(diffItem));
        });
    }

    /** only the hierarchical parent of all instances that implement CoachingDiffDecorateConnector should call this */
    decorate(diffItems: DiffItem[], approvalItems: ApprovalItem[]): void {
        this.unDecorate();

        this._diffItems = diffItems;
        this._approvalItems = approvalItems;

        this._decorate();
    }

    /** only the hierarchical parent of all instances that implement CoachingDiffDecorateConnector should call this */
    unDecorate(): void {
        this._runAll((instance, diffItem) => {
            instance.ddcUnDecorate &&
                ['change', 'accept', 'dispute'].forEach((state) => {
                    instance.ddcUnDecorate(diffItem, state as any);
                });
        });

        this._diffItems = null;
        this._approvalItems = null;
    }

    private _decorate(): void {
        this._runAll((instance, diffItem) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            instance.ddcDecorate &&
                instance.ddcDecorate(diffItem, this._approvalState(diffItem));
        });
    }

    private _runAll(
        predicate: (
            instance: CoachingDiffDecorateConnector,
            diffItem: DiffItem,
        ) => void,
    ): void {
        this._run((arr, items) => {
            arr.forEach((instance) => {
                items.forEach((diffItem) => {
                    predicate(instance, diffItem);
                });
            });
        });
    }

    private _runSingle(
        instance: CoachingDiffDecorateConnector,
        predicate: (diffItem: DiffItem) => void,
    ): void {
        this._run((arr, items) => {
            if (arr.includes(instance)) {
                items.forEach((diffItem) => {
                    predicate(diffItem);
                });
            }
        });
    }

    private _run(
        predicate: (
            arr: CoachingDiffDecorateConnector[],
            items: DiffItem[],
        ) => void,
    ): void {
        if (!this._diffItems) {
            return;
        }
        Object.keys(this._entityTypeInstanceMap)
            .map((e) => Number(e) as EntityType)
            .forEach((entityTypeId) => {
                const arr = this._entityTypeInstanceMap[entityTypeId];
                if (!arr) {
                    return;
                }

                const items = this._diffItems.filter(
                    (diffItem) => diffItem.entityTypeId === entityTypeId,
                );
                predicate(arr, items);
            });
    }

    private _approvalState(
        diffItem: DiffItem,
    ): 'change' | 'accept' | 'dispute' {
        let suffix: 'change' | 'accept' | 'dispute' = 'change';
        if (!this._approvalItems) {
            return suffix;
        }
        const approvalItem = this._approvalItems.find(
            (e) => e.diffItemId === diffItem.diffItemId,
        );
        if (!approvalItem) {
            return suffix;
        }
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (approvalItem && approvalItem.approved) {
            suffix = 'accept';
        } else if (approvalItem && approvalItem.approved === false) {
            suffix = 'dispute';
        }
        return suffix;
    }
}
